import type { DdwState } from '../types';

interface Props {
  state: DdwState;
  content: { heroBadge: string; heroTitle: string; heroSub: string; ctaPrimary: string };
  onPrimary(): void;
  onStartOver(): void;
}

/**
 * Welcome screen — entry point. Shows different CTA copy depending on
 * whether there's prior in-progress state in localStorage. Always lets
 * the buyer wipe and start over.
 */
export function Welcome({ state, content, onPrimary, onStartOver }: Props) {
  const hasProgress =
    state.phase1Complete || (state.lens.jobStatement || '').trim().length > 0;

  const ctaLabel = hasProgress
    ? state.phase1Complete
      ? `Resume — ${state.vendors.length} vendor${state.vendors.length === 1 ? '' : 's'}`
      : 'Resume your evaluation'
    : content.ctaPrimary;

  return (
    <>
      <section className="ddw-hero">
        <div className="ddw-hero-badge">{content.heroBadge}</div>
        <h1 dangerouslySetInnerHTML={{ __html: content.heroTitle }} />
        <p dangerouslySetInnerHTML={{ __html: content.heroSub }} />
        <div>
          <button type="button" className="ddw-hero-cta" onClick={onPrimary}>
            <span>{ctaLabel}</span>
            <svg width={16} height={16} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </button>
          {hasProgress && (
            <button type="button" className="ddw-hero-resume" onClick={onStartOver}>
              Start over
            </button>
          )}
        </div>
        <div className="ddw-hero-meta">
          Vendor-agnostic · Free · No account required · Stays in your browser
        </div>
      </section>

      <section className="ddw-how-strip">
        <div className="ddw-how-inner">
          <h2>How it works</h2>
          <p className="ddw-how-sub">
            About 15 minutes. Returnable — add vendors over time as your evaluation progresses.
          </p>
          <div className="ddw-how-grid">
            <div className="ddw-how-card">
              <span className="ddw-how-num">Step 1</span>
              <h3>Build your lens</h3>
              <p>
                Five short questions get you to a personal evaluation framework: the job you're
                trying to do, the outcomes that matter, your flexibility needs, and the vendor
                signals you care about.
              </p>
            </div>
            <div className="ddw-how-card">
              <span className="ddw-how-num">Step 2</span>
              <h3>Add vendors and score</h3>
              <p>
                Add the vendors you're evaluating by name. Score each one against your lens in
                about two minutes. Add more whenever you find them.
              </p>
            </div>
            <div className="ddw-how-card">
              <span className="ddw-how-num">Step 3</span>
              <h3>Compare and decide</h3>
              <p>
                Print a per-vendor report, a side-by-side comparison, or your full playbook to
                share with the rest of your decision team.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
